import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDocuments, usePolicyInfo } from 'all-in-one-shared-api';

import GeneralDocuments from './GeneralDocuments';
import ClaimDocuments from './ClaimDocuments';
import OthersDocuments from './OthersDocuments';
import ClaimSettlementNotice from './ClaimSettlementNotice';

import { useKeycloakReady } from 'hooks/useKeycloakReady';

import { isLocaleEnglish, getLatestRenewalNumbers } from 'utils';
import {
  formatClaimSettlementNotice,
  getAvailableClaimForms,
  getAvailableDocuments,
  getOthersDocuments,
} from '../utils';

function DocumentsBody() {
  const { i18n } = useTranslation();
  const { ready, token, tokenParsed } = useKeycloakReady();

  const { shouldFetch: policyShouldFetch, data: policyInfoData } = usePolicyInfo('usePolicyInfo', token || '');

  const isEnglish = isLocaleEnglish(i18n.language);

  const memberType = tokenParsed ? tokenParsed.memberType : '';

  const renewalNumbers = useMemo(() => {
    return policyInfoData ? getLatestRenewalNumbers(policyInfoData, memberType) : [];
  }, [policyInfoData, memberType]);

  const { shouldFetch: documentsShouldFetch, data: documentsData } = useDocuments(
    { name: 'useDocuments', params: { renewalNumbers } },
    token || '',
    renewalNumbers,
  );

  useEffect(() => {
    policyShouldFetch(ready);
    documentsShouldFetch(ready && renewalNumbers.length > 0);
  }, [ready, renewalNumbers, policyShouldFetch, documentsShouldFetch]);

  const availableDocuments = getAvailableDocuments(
    documentsData,
    policyInfoData,
    memberType,
    renewalNumbers,
    isEnglish,
  );
  const availableClaimForms = getAvailableClaimForms(documentsData);
  const formattedClaimSettlementNotices = formatClaimSettlementNotice(documentsData?.claimSettlementNotice ?? []);
  const otherDocuments = getOthersDocuments(documentsData);

  return (
    <>
      <GeneralDocuments generalDocuments={availableDocuments} />
      <ClaimDocuments claimDocuments={availableClaimForms} />
      {formattedClaimSettlementNotices.length > 0 && (
        <ClaimSettlementNotice claimSettlementNotices={formattedClaimSettlementNotices} />
      )}
      {otherDocuments.length > 0 && <OthersDocuments OthersDocuments={otherDocuments} />}
    </>
  );
}

export default DocumentsBody;
